import 'react-phone-number-input/style.css';

import { sizeWithTextVariantMapping } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput.v2';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { TextV2 as Text2 } from '@dealstack/frontend/shared/components/TextV2/TextV2';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { rem } from '@mantine/core';
import { debounce } from 'lodash';
import { useCallback } from 'react';
import { Control, useController, useWatch } from 'react-hook-form';
import PhoneInputWithCountrySelect, { parsePhoneNumber } from 'react-phone-number-input';
import styled, { css } from 'styled-components';
import { PredefinedTextVariants } from '@dealstack/frontend/shared/components/fundament/Text/Text.types';
import { PredefinedV2TextVariants } from '@dealstack/frontend/shared/components/TextV2/TextV2.types';
import { StringInputV2SizeValues } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput.v2.types';
import { CountrySelect } from './CountrySelect';

export interface Props {
	name: string;
	phonePrefixName: string;
	placeholder?: string;
	disabled?: boolean;
	readOnly?: boolean;
	label?: string | React.ReactNode;
	size: StringInputV2SizeValues;
	control?: Control;
}

export const PhoneNumberInput = ({
	name,
	disabled,
	readOnly,
	label,
	phonePrefixName,
	control,
	placeholder,
	size = 'small',
}: Props) => {
	const tCommon = useTranslationCommonUi();

	const phonePrefixField = useController({
		name: phonePrefixName,
		control: control as Control,
		defaultValue: '44',
	});
	const phoneNumberField = useController({
		name,
		control: control as Control,
	});

	useWatch({ control: control as Control });

	const debouncedChange = useCallback(
		debounce((value) => {
			if (!value) {
				return;
			}

			const nbr = parsePhoneNumber(value);
			phoneNumberField.field.onChange(value);
			const phonePrefix = nbr?.countryCallingCode;

			phonePrefixField.field.onChange(phonePrefix ? `+${phonePrefix}` : undefined);
		}, 1000),
		[phoneNumberField.field.onChange]
	);

	return (
		<div style={{ width: '100%' }}>
			{label && (
				<LabelWrapper>
					<Text
						variant={
							sizeWithTextVariantMapping[size]
								.labelTextVariant as PredefinedTextVariants
						}
						disableVariantMapping
					>
						{label}
					</Text>
				</LabelWrapper>
			)}
			<PhoneWrapper
				data-testid={INVESTOR_TEST_ELEMENT_ID.ACCEPT_INVITATION_PHONE_NUMBER_INPUT}
			>
				{readOnly && <StyledIcon $size={size} variant="phone" />}

				<StyledPhoneInputWithCountrySelect
					placeholder={placeholder || tCommon('phone_number.placeholder')}
					readOnly={readOnly}
					countrySelectComponent={CountrySelect}
					defaultCountry="GB"
					value={phoneNumberField.field.value}
					onChange={debouncedChange}
					disabled={disabled || readOnly}
				/>
			</PhoneWrapper>
			{phoneNumberField.fieldState.error?.message && (
				<ErrorText
					variant={
						sizeWithTextVariantMapping[size]
							.labelTextVariant as PredefinedV2TextVariants
					}
				>
					{phoneNumberField.fieldState.error?.message?.toString()}
				</ErrorText>
			)}
		</div>
	);
};

const StyledIcon = styled(Icon)<{ $size: StringInputV2SizeValues }>`
	${({ $size }) => css`
		${Icon} {
			${sizeWithTextVariantMapping[$size].hintIconSize};
		}
	`}
`;

const StyledPhoneInputWithCountrySelect = styled(PhoneInputWithCountrySelect)`
	border-radius: 4px;
	padding-right: 0.5rem;
	display: flex;
	width: 100%;
	height: 2.5rem;
	border: ${(props) => (props.readOnly ? '' : '0.0625rem solid #D3D8DA')};

	.PhoneInputInput {
		border: 0px;
	}

	${({ readOnly }) =>
		readOnly &&
		css`
			.PhoneInputInput {
				border: none;
				background-color: transparent;
				color: ${({ theme }) => theme.v2.color.neutral[1100]};
				height: ${rem(10)};
				padding: ${rem(8)};
			}
		`}
`;

const PhoneWrapper = styled.div`
	display: flex;
	align-items: center;
	background: white;
`;

const LabelWrapper = styled.div`
	color: ${({ theme }) => theme.v2.color.neutral[1100]};
	margin-bottom: ${rem(8)};
`;

const ErrorText = styled(Text2)`
	margin-top: ${rem(8)};
	color: ${({ theme }) => theme.v2.color.error[800]};
`;
